import type { ChangeEvent } from 'react';
import { memo, useCallback, useMemo, useState } from 'react';
import { Badge, Button, Col, Dropdown, Form, Row } from 'react-bootstrap';

import type { FacilityFilterOptions } from '@/services/FacilityService';
import type { FacilityFilters as FacilityFiltersType } from '@/types';

import styles from './FacilityFilters.module.scss';

interface FacilityFiltersProps {
  readonly filters: FacilityFiltersType;
  readonly onFiltersChange: (filters: FacilityFiltersType) => void;
  readonly filterOptions: FacilityFilterOptions;
  readonly loading?: boolean;
}

function formatLabel(value: string): string {
  return value
    .split(/[_\s]+/)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
}

const PROVINCE_LABELS: Record<string, string> = {
  BC: 'British Columbia',
  ON: 'Ontario',
  AB: 'Alberta',
  SK: 'Saskatchewan',
  MB: 'Manitoba',
  QC: 'Quebec',
  NB: 'New Brunswick',
  NS: 'Nova Scotia',
  PE: 'Prince Edward Island',
  NL: 'Newfoundland and Labrador',
  YT: 'Yukon',
  NT: 'Northwest Territories',
  NU: 'Nunavut',
};

interface MultiSelectDropdownProps {
  readonly label: string;
  readonly options: readonly { value: string; label: string }[];
  readonly selected: readonly string[];
  readonly onChange: (selected: readonly string[]) => void;
  readonly disabled?: boolean;
}

const MultiSelectDropdown = memo<MultiSelectDropdownProps>(
  function MultiSelectDropdown({
    label,
    options,
    selected,
    onChange,
    disabled,
  }) {
    const handleToggle = (value: string): void => {
      if (selected.includes(value)) {
        onChange(selected.filter((v) => v !== value));
      } else {
        onChange([...selected, value]);
      }
    };

    const handleSelectAll = (): void => {
      if (selected.length === options.length) {
        onChange([]);
      } else {
        onChange(options.map((o) => o.value));
      }
    };

    const displayText =
      selected.length === 0
        ? `All ${label}`
        : selected.length === 1
          ? options.find((o) => o.value === selected[0])?.label || selected[0]
          : `${selected.length} selected`;

    return (
      <Dropdown className={styles.multiSelect}>
        <Dropdown.Toggle
          variant="outline-secondary"
          disabled={disabled || options.length === 0}
          className={styles.dropdownToggle}
        >
          {displayText}
          {selected.length > 0 && (
            <Badge bg="primary" className="ms-2">
              {selected.length}
            </Badge>
          )}
        </Dropdown.Toggle>
        <Dropdown.Menu className={styles.dropdownMenu}>
          <div className={styles.selectAllRow}>
            <Form.Check
              type="checkbox"
              id={`${label}-select-all`}
              label="Select All"
              checked={selected.length === options.length}
              onChange={handleSelectAll}
              className={styles.checkItem}
            />
          </div>
          <Dropdown.Divider />
          {options.map((option) => (
            <div key={option.value} className={styles.optionRow}>
              <Form.Check
                type="checkbox"
                id={`${label}-${option.value}`}
                label={option.label}
                checked={selected.includes(option.value)}
                onChange={() => handleToggle(option.value)}
                className={styles.checkItem}
              />
            </div>
          ))}
        </Dropdown.Menu>
      </Dropdown>
    );
  }
);

export const FacilityFilters = memo<FacilityFiltersProps>(
  function FacilityFilters({
    filters,
    onFiltersChange,
    filterOptions,
    loading,
  }) {
    const [searchInput, setSearchInput] = useState(filters.search || '');

    const provinceOptions = useMemo(() => {
      return filterOptions.provinces.map((p) => ({
        value: p,
        label: PROVINCE_LABELS[p] || p,
      }));
    }, [filterOptions.provinces]);

    const statusOptions = useMemo(() => {
      return filterOptions.statuses.map((s) => ({
        value: s,
        label: formatLabel(s),
      }));
    }, [filterOptions.statuses]);

    const facilityTypeOptions = useMemo(() => {
      return filterOptions.facility_types.map((t) => ({
        value: t,
        label: t,
      }));
    }, [filterOptions.facility_types]);

    const handleProvincesChange = useCallback(
      (selected: readonly string[]) => {
        onFiltersChange({
          ...filters,
          provinces: selected.length > 0 ? selected : undefined,
        });
      },
      [filters, onFiltersChange]
    );

    const handleStatusesChange = useCallback(
      (selected: readonly string[]) => {
        onFiltersChange({
          ...filters,
          statuses: selected.length > 0 ? selected : undefined,
        });
      },
      [filters, onFiltersChange]
    );

    const handleFacilityTypesChange = useCallback(
      (selected: readonly string[]) => {
        onFiltersChange({
          ...filters,
          facility_types: selected.length > 0 ? selected : undefined,
        });
      },
      [filters, onFiltersChange]
    );

    const handleSearchInputChange = useCallback(
      (e: ChangeEvent<HTMLInputElement>) => {
        setSearchInput(e.target.value);
      },
      []
    );

    const handleSearchSubmit = useCallback(
      (e: React.FormEvent) => {
        e.preventDefault();
        onFiltersChange({
          ...filters,
          search: searchInput || undefined,
        });
      },
      [filters, onFiltersChange, searchInput]
    );

    const handleClearFilters = useCallback(() => {
      setSearchInput('');
      onFiltersChange({});
    }, [onFiltersChange]);

    const hasActiveFilters =
      (filters.provinces && filters.provinces.length > 0) ||
      (filters.statuses && filters.statuses.length > 0) ||
      (filters.facility_types && filters.facility_types.length > 0) ||
      filters.search;

    return (
      <Form onSubmit={handleSearchSubmit}>
        <Row className="g-3 align-items-end">
          <Col xs={12} md={3}>
            <Form.Group>
              <Form.Label>Search</Form.Label>
              <div className="d-flex gap-2">
                <Form.Control
                  type="text"
                  placeholder="Search by name or operator..."
                  value={searchInput}
                  onChange={handleSearchInputChange}
                  disabled={loading}
                />
                <Button
                  type="submit"
                  variant="primary"
                  disabled={loading}
                  style={{ whiteSpace: 'nowrap' }}
                >
                  Search
                </Button>
              </div>
            </Form.Group>
          </Col>
          <Col xs={6} md={2}>
            <Form.Group>
              <Form.Label>Province</Form.Label>
              <MultiSelectDropdown
                label="Provinces"
                options={provinceOptions}
                selected={filters.provinces || []}
                onChange={handleProvincesChange}
                disabled={loading}
              />
            </Form.Group>
          </Col>
          <Col xs={6} md={2}>
            <Form.Group>
              <Form.Label>Status</Form.Label>
              <MultiSelectDropdown
                label="Statuses"
                options={statusOptions}
                selected={filters.statuses || []}
                onChange={handleStatusesChange}
                disabled={loading}
              />
            </Form.Group>
          </Col>
          <Col xs={6} md={2}>
            <Form.Group>
              <Form.Label>Type</Form.Label>
              <MultiSelectDropdown
                label="Types"
                options={facilityTypeOptions}
                selected={filters.facility_types || []}
                onChange={handleFacilityTypesChange}
                disabled={loading}
              />
            </Form.Group>
          </Col>
          <Col xs={6} md={1}>
            {hasActiveFilters && (
              <Button
                variant="outline-secondary"
                onClick={handleClearFilters}
                disabled={loading}
                className="w-100"
              >
                Clear
              </Button>
            )}
          </Col>
        </Row>
      </Form>
    );
  }
);
